/**
 * Canonical action descriptor for shell command bars and the responsive
 * archetypes.
 *
 * Before this type existed, WorkbenchShell, DetailShell, PageHeader, and
 * FormShell each carried a slightly different `actions` shape (`onClick` vs
 * icon-component vs free-form ReactNode footers), so a view migrating between
 * shells had to re-map its action list every time. `ActionDefinition` is the
 * one shape new code should use; the shells accept it directly alongside
 * their legacy shapes via {@link toWorkbenchActions}.
 *
 * Pure module — JSX-free so the mapping logic runs under the
 * `node --test` strip-types sweep.
 */
import type * as React from 'react';
import type { WorkbenchAction } from './workbench-shell';

/** Visual emphasis for an action. Maps onto Button variants per shell. */
export type ActionIntent = 'default' | 'primary' | 'destructive';

export interface ActionDefinition {
  /** Stable id (React key + test selector). */
  id: string;
  /** Visible label. Keep short — also used in mobile action sheets. */
  label: string;
  /** Optional leading icon node. */
  icon?: React.ReactNode;
  /** Visual emphasis. `primary` for the single main action per surface. */
  intent?: ActionIntent;
  /** Invoked when the user activates the action. */
  onSelect: () => void;
  /** Render disabled (still visible, not interactive). */
  disabled?: boolean;
  /** Omit from rendering entirely (permission / lifecycle gating). */
  hidden?: boolean;
}

/** Either shape a shell command bar accepts during the migration window. */
export type ShellAction = ActionDefinition | WorkbenchAction;

/** Discriminate the canonical shape from the legacy WorkbenchAction. */
export function isActionDefinition(action: ShellAction): action is ActionDefinition {
  return typeof (action as ActionDefinition).onSelect === 'function';
}

/** Drop actions gated out via `hidden`. Legacy WorkbenchActions pass through. */
export function visibleActions<A extends ShellAction>(actions: readonly A[]): A[] {
  return actions.filter((a) => !(isActionDefinition(a) && a.hidden));
}

/** Map an ActionIntent onto the Button variant vocabulary the shells use. */
export function intentToButtonVariant(
  intent: ActionIntent | undefined,
): NonNullable<WorkbenchAction['variant']> {
  switch (intent) {
    case 'primary':
      return 'default';
    case 'destructive':
      return 'destructive';
    default:
      return 'outline';
  }
}

/** Normalize one action of either shape into the WorkbenchAction the shells render. */
export function toWorkbenchAction(action: ShellAction): WorkbenchAction {
  if (!isActionDefinition(action)) return action;
  return {
    id: action.id,
    label: action.label,
    icon: action.icon,
    onClick: action.onSelect,
    variant: intentToButtonVariant(action.intent),
    disabled: action.disabled,
  };
}

/**
 * Normalize a mixed action list for shell rendering: hidden
 * ActionDefinitions are dropped, the rest become WorkbenchActions.
 */
export function toWorkbenchActions(actions: readonly ShellAction[]): WorkbenchAction[] {
  return visibleActions(actions).map(toWorkbenchAction);
}
